import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  EyeIcon, EyeSlashIcon, ArrowRightIcon, ArrowLeftIcon,
  CheckCircleIcon, ExclamationTriangleIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../services/api';

// ── Schemas por etapa ──────────────────────────────────────────────────
const step1Schema = z.object({
  name:            z.string().min(2, 'Nome muito curto').max(255),
  password:        z.string().min(8, 'Mínimo 8 caracteres')
                    .regex(/^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/, 'Precisa de 1 maiúscula, 1 número e 1 caractere especial'),
  confirmPassword: z.string(),
  phone:           z.string().min(10, 'Telefone inválido').max(20),
  cpf:             z.string().regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido'),
  birthDate:       z.string().min(1, 'Data obrigatória'),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

const step2Schema = z.object({
  addressZip:          z.string().regex(/^\d{5}-\d{3}$/, 'CEP inválido'),
  addressStreet:       z.string().min(2, 'Logradouro obrigatório'),
  addressNumber:       z.string().min(1, 'Número obrigatório'),
  addressComplement:   z.string().optional(),
  addressNeighborhood: z.string().min(2, 'Bairro obrigatório'),
  addressCity:         z.string().min(2, 'Cidade obrigatória'),
  addressState:        z.string().length(2, 'UF deve ter 2 letras'),
});

const step3Schema = z.object({
  investmentObjective:       z.number().min(1).max(4),
  investmentHorizon:         z.number().min(1).max(4),
  lossReaction:              z.number().min(1).max(4),
  financialKnowledge:        z.number().min(1).max(4),
  monthlyIncome:             z.number().min(1).max(4),
  investmentPercentage:      z.number().min(1).max(4),
  variableIncomeExperience:  z.number().min(1).max(4),
});

type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;
type Step3Data = z.infer<typeof step3Schema>;

// ── Questionário ────────────────────────────────────────────────────────
const QUESTIONS: { key: keyof Step3Data; question: string; options: string[] }[] = [
  {
    key: 'investmentObjective',
    question: '1. Qual é o seu principal objetivo ao investir?',
    options: [
      'Preservar meu capital com segurança',
      'Obter renda com pequenas oscilações',
      'Crescimento moderado, aceitando algum risco',
      'Crescimento agressivo, com alto risco',
    ],
  },
  {
    key: 'investmentHorizon',
    question: '2. Por quanto tempo pretende manter seus investimentos?',
    options: ['Menos de 1 ano', 'De 1 a 3 anos', 'De 3 a 5 anos', 'Mais de 5 anos'],
  },
  {
    key: 'lossReaction',
    question: '3. Se sua carteira caísse 20%, o que você faria?',
    options: [
      'Resgataria tudo imediatamente',
      'Ficaria preocupado e reduziria posições',
      'Manteria e aguardaria a recuperação',
      'Aproveitaria para comprar mais',
    ],
  },
  {
    key: 'financialKnowledge',
    question: '4. Como você avalia seu conhecimento sobre investimentos?',
    options: [
      'Nenhum — nunca investi',
      'Básico — poupança e CDB',
      'Intermediário — fundos e ações',
      'Avançado — derivativos e renda variável',
    ],
  },
  {
    key: 'monthlyIncome',
    question: '5. Qual é a sua renda mensal aproximada?',
    options: ['Até R$ 3.000', 'De R$ 3.001 a R$ 8.000', 'De R$ 8.001 a R$ 20.000', 'Acima de R$ 20.000'],
  },
  {
    key: 'investmentPercentage',
    question: '6. Que percentual da sua renda pretende investir mensalmente?',
    options: ['Até 5%', 'De 6% a 15%', 'De 16% a 30%', 'Acima de 30%'],
  },
  {
    key: 'variableIncomeExperience',
    question: '7. Já investiu em renda variável (ações, FIIs, ETFs)?',
    options: [
      'Nunca',
      'Sim, raramente',
      'Sim, com regularidade',
      'É meu principal veículo de investimento',
    ],
  },
];

function getProfileType(score: number) {
  if (score <= 12) return { label: 'CONSERVADOR', color: '#22d3ee', desc: 'Prioriza segurança e preservação do capital.' };
  if (score <= 18) return { label: 'MODERADO',    color: '#a3e635', desc: 'Aceita risco moderado em busca de retorno.' };
  if (score <= 24) return { label: 'ARROJADO',    color: '#fb923c', desc: 'Tolera volatilidade para maiores retornos.' };
  return              { label: 'AGRESSIVO',   color: '#f43f5e', desc: 'Busca máximo retorno aceitando alto risco.' };
}

// ── Utilitários de máscara ────────────────────────────────────────────
function maskCPF(v: string) {
  return v.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2').slice(0, 14);
}
function maskPhone(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
}
function maskCEP(v: string) {
  return v.replace(/\D/g, '').replace(/(\d{5})(\d{0,3})/, '$1-$2').slice(0, 9);
}

const PARTICLES = Array.from({ length: 16 }, (_, i) => ({
  id: i, left: `${((i * 17 + 7) % 97) + 1}%`, bottom: `${(i * 13) % 30}px`,
  delay: `${(i * 0.6) % 9}s`, duration: `${7 + (i % 5)}s`,
  size: i % 4 === 0 ? 3 : 1.5, drift: `${(i % 2 === 0 ? 1 : -1) * (8 + (i % 18))}px`,
}));

const FIELD_STYLE: React.CSSProperties = {
  width: '100%', padding: '11px 14px', background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(0,212,255,0.15)', borderRadius: 8, color: '#fff',
  fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
};
const LABEL_STYLE: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.5)',
  marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase',
};
const ERR: React.CSSProperties = { marginTop: 4, fontSize: 11, color: 'rgb(239,68,68)' };

// ── Componente principal ──────────────────────────────────────────────
export default function CompleteRegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [tokenStatus, setTokenStatus] = useState<'validating' | 'valid' | 'invalid'>('validating');
  const [prefillEmail, setPrefillEmail] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Dados acumulados entre etapas
  const [data1, setData1] = useState<Step1Data | null>(null);
  const [data2, setData2] = useState<Step2Data | null>(null);
  const [answers, setAnswers] = useState<Partial<Step3Data>>({});

  const form1 = useForm<Step1Data>({ resolver: zodResolver(step1Schema) });
  const form2 = useForm<Step2Data>({ resolver: zodResolver(step2Schema) });

  // Valida token ao montar
  useEffect(() => {
    if (!token) { setTokenStatus('invalid'); return; }
    authApi.validateRegistrationToken(token)
      .then((res: { valid: boolean; email: string }) => {
        setPrefillEmail(res.email);
        setTokenStatus('valid');
      })
      .catch(() => setTokenStatus('invalid'));
  }, [token]);

  const onStep1 = (d: Step1Data) => { setData1(d); setStep(2); };
  const onStep2 = (d: Step2Data) => { setData2(d); setStep(3); };

  const setAnswer = useCallback((key: keyof Step3Data, val: number) => {
    setAnswers((prev) => ({ ...prev, [key]: val }));
  }, []);

  const answeredCount = Object.keys(answers).length;
  const score = Object.values(answers).reduce((s, v) => s + (v ?? 0), 0);
  const profile = answeredCount === 7 ? getProfileType(score) : null;

  const onSubmit = async () => {
    if (answeredCount < 7 || !data1 || !data2) return;
    setSubmitting(true);
    try {
      const result = await authApi.completeRegistration({
        token,
        name:            data1.name,
        email:           prefillEmail,
        password:        data1.password,
        phone:           data1.phone,
        cpf:             data1.cpf,
        birthDate:       data1.birthDate,
        addressZip:          data2.addressZip,
        addressStreet:       data2.addressStreet,
        addressNumber:       data2.addressNumber,
        addressComplement:   data2.addressComplement,
        addressNeighborhood: data2.addressNeighborhood,
        addressCity:         data2.addressCity,
        addressState:        data2.addressState,
        ...answers,
      });
      localStorage.setItem('accessToken',  result.accessToken);
      localStorage.setItem('refreshToken', result.refreshToken);
      navigate('/dashboard');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchCep = async (cep: string) => {
    const raw = cep.replace(/\D/g, '');
    if (raw.length !== 8) return;
    setLoadingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
      const data = await res.json();
      if (!data.erro) {
        form2.setValue('addressStreet',       data.logradouro);
        form2.setValue('addressNeighborhood', data.bairro);
        form2.setValue('addressCity',         data.localidade);
        form2.setValue('addressState',        data.uf);
      }
    } catch { /* silently ignore */ }
    finally { setLoadingCep(false); }
  };

  // ── Tela de validação / inválido ─────────────────────────────────────
  if (tokenStatus === 'validating') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0f1e' }}>
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
          <div style={{ width: 40, height: 40, border: '2px solid rgba(0,212,255,0.3)', borderTopColor: '#00d4ff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          Validando link...
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (tokenStatus === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0f1e' }}>
        <div style={{ textAlign: 'center', maxWidth: 340, padding: 32 }}>
          <ExclamationTriangleIcon style={{ width: 52, height: 52, color: '#f43f5e', margin: '0 auto 16px' }} />
          <h2 style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Link inválido ou expirado</h2>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, lineHeight: 1.7, marginBottom: 24 }}>
            Este link não é mais válido. Solicite um novo link de cadastro.
          </p>
          <button onClick={() => navigate('/register')} style={{ padding: '12px 28px', background: 'linear-gradient(135deg,#00b4d8,#00d4ff)', border: 'none', borderRadius: 8, color: '#0a0f1e', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Solicitar novo link
          </button>
        </div>
      </div>
    );
  }

  // ── Indicador de etapas ────────────────────────────────────────────
  const StepIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28, gap: 0 }}>
      {[1, 2, 3].map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center', flex: i < 2 ? 1 : 'unset' }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, flexShrink: 0,
            background: step > s ? '#00d4ff' : step === s ? 'rgba(0,212,255,0.2)' : 'rgba(255,255,255,0.05)',
            border: step >= s ? '1.5px solid #00d4ff' : '1.5px solid rgba(255,255,255,0.1)',
            color: step > s ? '#0a0f1e' : step === s ? '#00d4ff' : 'rgba(255,255,255,0.3)',
          }}>
            {step > s ? <CheckCircleIcon style={{ width: 14, height: 14 }} /> : s}
          </div>
          {i < 2 && <div style={{ flex: 1, height: 1, background: step > s ? '#00d4ff' : 'rgba(255,255,255,0.08)', margin: '0 4px' }} />}
        </div>
      ))}
    </div>
  );

  const stepLabels = ['Dados Pessoais', 'Endereço', 'Perfil do Investidor'];

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: '#0a0f1e' }}>
      {/* BG */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `linear-gradient(rgba(0,212,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.025) 1px, transparent 1px)`, backgroundSize: '50px 50px', animation: 'gridScroll 20s linear infinite' }} />
      <div className="absolute pointer-events-none" style={{ top: '-15%', right: '-10%', width: '55vw', height: '55vw', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,212,255,0.07) 0%, transparent 70%)', animation: 'orb1 12s ease-in-out infinite' }} />
      <div className="absolute pointer-events-none" style={{ bottom: '-10%', left: '-8%', width: '45vw', height: '45vw', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,100,180,0.08) 0%, transparent 70%)', animation: 'orb2 15s ease-in-out infinite' }} />
      {PARTICLES.map((p) => (
        <div key={p.id} className="absolute pointer-events-none rounded-full" style={{ left: p.left, bottom: p.bottom, width: p.size, height: p.size, background: '#00d4ff', opacity: 0, animation: `particleUp ${p.duration} ${p.delay} infinite linear`, '--drift': p.drift } as React.CSSProperties} />
      ))}

      <div className="relative z-10 w-full px-4 py-8" style={{ maxWidth: step === 3 ? 560 : 440, animation: 'fadeSlideUp 0.5s ease-out both' }}>

        {/* Logo */}
        <div style={{ animation: 'logoFloat 6s ease-in-out infinite', marginBottom: '2px' }}>
          <img src="/img/inoveitech_logo.svg" alt="IncluirTech" className="w-full" style={{ borderRadius: '12px 12px 0 0', display: 'block' }} />
        </div>

        <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(0,212,255,0.15)', borderTop: 'none', borderRadius: '0 0 16px 16px', padding: '32px', boxShadow: '0 0 0 1px rgba(0,212,255,0.1), 0 20px 60px rgba(0,0,0,0.6)' }}>

          <StepIndicator />

          {/* Título da etapa */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(0,212,255,0.15)' }} />
            <span style={{ color: 'rgba(0,212,255,0.5)', fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: 'uppercase' }}>
              {stepLabels[step - 1]}
            </span>
            <div style={{ flex: 1, height: 1, background: 'rgba(0,212,255,0.15)' }} />
          </div>

          {/* ── ETAPA 1: Dados pessoais ──────────────────────────── */}
          {step === 1 && (
            <form onSubmit={form1.handleSubmit(onStep1)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* E-mail (somente leitura) */}
              <div>
                <label style={LABEL_STYLE}>E-mail</label>
                <input readOnly value={prefillEmail} style={{ ...FIELD_STYLE, opacity: 0.6, cursor: 'default' }} />
              </div>

              {/* Nome */}
              <div>
                <label style={LABEL_STYLE}>Nome completo</label>
                <input {...form1.register('name')} placeholder="João da Silva" style={{ ...FIELD_STYLE, borderColor: form1.formState.errors.name ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form1.formState.errors.name ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                {form1.formState.errors.name && <p style={ERR}>{form1.formState.errors.name.message}</p>}
              </div>

              {/* Senha */}
              <div>
                <label style={LABEL_STYLE}>Senha</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPwd ? 'text' : 'password'} {...form1.register('password')} placeholder="Senha@123" style={{ ...FIELD_STYLE, paddingRight: 44, borderColor: form1.formState.errors.password ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form1.formState.errors.password ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: 0 }}>
                    {showPwd ? <EyeSlashIcon style={{ width: 16, height: 16 }} /> : <EyeIcon style={{ width: 16, height: 16 }} />}
                  </button>
                </div>
                {form1.formState.errors.password && <p style={ERR}>{form1.formState.errors.password.message}</p>}
              </div>

              {/* Confirmar senha */}
              <div>
                <label style={LABEL_STYLE}>Confirmar senha</label>
                <div style={{ position: 'relative' }}>
                  <input type={showConfirm ? 'text' : 'password'} {...form1.register('confirmPassword')} placeholder="••••••••" style={{ ...FIELD_STYLE, paddingRight: 44, borderColor: form1.formState.errors.confirmPassword ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form1.formState.errors.confirmPassword ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: 0 }}>
                    {showConfirm ? <EyeSlashIcon style={{ width: 16, height: 16 }} /> : <EyeIcon style={{ width: 16, height: 16 }} />}
                  </button>
                </div>
                {form1.formState.errors.confirmPassword && <p style={ERR}>{form1.formState.errors.confirmPassword.message}</p>}
              </div>

              {/* Telefone + CPF */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={LABEL_STYLE}>Telefone</label>
                  <input {...form1.register('phone')} placeholder="(11) 99999-9999"
                    onChange={(e) => form1.setValue('phone', maskPhone(e.target.value))}
                    style={{ ...FIELD_STYLE, borderColor: form1.formState.errors.phone ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form1.formState.errors.phone ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  {form1.formState.errors.phone && <p style={ERR}>{form1.formState.errors.phone.message}</p>}
                </div>
                <div>
                  <label style={LABEL_STYLE}>CPF</label>
                  <input {...form1.register('cpf')} placeholder="000.000.000-00"
                    onChange={(e) => form1.setValue('cpf', maskCPF(e.target.value))}
                    style={{ ...FIELD_STYLE, borderColor: form1.formState.errors.cpf ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form1.formState.errors.cpf ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  {form1.formState.errors.cpf && <p style={ERR}>{form1.formState.errors.cpf.message}</p>}
                </div>
              </div>

              {/* Data de nascimento */}
              <div>
                <label style={LABEL_STYLE}>Data de nascimento</label>
                <input type="date" {...form1.register('birthDate')} style={{ ...FIELD_STYLE, colorScheme: 'dark', borderColor: form1.formState.errors.birthDate ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form1.formState.errors.birthDate ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                {form1.formState.errors.birthDate && <p style={ERR}>{form1.formState.errors.birthDate.message}</p>}
              </div>

              <button type="submit" style={{ marginTop: 8, padding: '13px 24px', background: 'linear-gradient(135deg,#00b4d8,#00d4ff)', border: 'none', borderRadius: 8, color: '#0a0f1e', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 24px rgba(0,212,255,0.35)' }}>
                Próximo <ArrowRightIcon style={{ width: 15, height: 15 }} />
              </button>
            </form>
          )}

          {/* ── ETAPA 2: Endereço ─────────────────────────────────── */}
          {step === 2 && (
            <form onSubmit={form2.handleSubmit(onStep2)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* CEP */}
              <div>
                <label style={LABEL_STYLE}>CEP</label>
                <div style={{ position: 'relative' }}>
                  <input {...form2.register('addressZip')} placeholder="00000-000"
                    onChange={(e) => {
                      const v = maskCEP(e.target.value);
                      form2.setValue('addressZip', v);
                      if (v.replace(/\D/g, '').length === 8) fetchCep(v);
                    }}
                    style={{ ...FIELD_STYLE, paddingRight: 40, borderColor: form2.formState.errors.addressZip ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form2.formState.errors.addressZip ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  {loadingCep && <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, border: '2px solid rgba(0,212,255,0.3)', borderTopColor: '#00d4ff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />}
                  {!loadingCep && <MagnifyingGlassIcon style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: 'rgba(0,212,255,0.3)' }} />}
                </div>
                {form2.formState.errors.addressZip && <p style={ERR}>{form2.formState.errors.addressZip.message}</p>}
              </div>

              {/* Logradouro + Número */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12 }}>
                <div>
                  <label style={LABEL_STYLE}>Logradouro</label>
                  <input {...form2.register('addressStreet')} placeholder="Avenida Paulista" style={{ ...FIELD_STYLE, borderColor: form2.formState.errors.addressStreet ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form2.formState.errors.addressStreet ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  {form2.formState.errors.addressStreet && <p style={ERR}>{form2.formState.errors.addressStreet.message}</p>}
                </div>
                <div style={{ width: 90 }}>
                  <label style={LABEL_STYLE}>Número</label>
                  <input {...form2.register('addressNumber')} placeholder="1000" style={{ ...FIELD_STYLE, borderColor: form2.formState.errors.addressNumber ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form2.formState.errors.addressNumber ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  {form2.formState.errors.addressNumber && <p style={ERR}>{form2.formState.errors.addressNumber.message}</p>}
                </div>
              </div>

              {/* Complemento */}
              <div>
                <label style={LABEL_STYLE}>Complemento <span style={{ color: 'rgba(255,255,255,0.25)', fontStyle: 'italic', fontSize: 10, textTransform: 'none', letterSpacing: 0 }}>(opcional)</span></label>
                <input {...form2.register('addressComplement')} placeholder="Apto 42, Bloco B" style={FIELD_STYLE} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.15)'; }} />
              </div>

              {/* Bairro */}
              <div>
                <label style={LABEL_STYLE}>Bairro</label>
                <input {...form2.register('addressNeighborhood')} placeholder="Bela Vista" style={{ ...FIELD_STYLE, borderColor: form2.formState.errors.addressNeighborhood ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form2.formState.errors.addressNeighborhood ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                {form2.formState.errors.addressNeighborhood && <p style={ERR}>{form2.formState.errors.addressNeighborhood.message}</p>}
              </div>

              {/* Cidade + UF */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 12 }}>
                <div>
                  <label style={LABEL_STYLE}>Cidade</label>
                  <input {...form2.register('addressCity')} placeholder="São Paulo" style={{ ...FIELD_STYLE, borderColor: form2.formState.errors.addressCity ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form2.formState.errors.addressCity ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  {form2.formState.errors.addressCity && <p style={ERR}>{form2.formState.errors.addressCity.message}</p>}
                </div>
                <div>
                  <label style={LABEL_STYLE}>UF</label>
                  <input {...form2.register('addressState')} placeholder="SP" maxLength={2} style={{ ...FIELD_STYLE, textTransform: 'uppercase', borderColor: form2.formState.errors.addressState ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)' }} onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }} onBlur={(e) => { e.target.style.borderColor = form2.formState.errors.addressState ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }} />
                  {form2.formState.errors.addressState && <p style={ERR}>{form2.formState.errors.addressState.message}</p>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="button" onClick={() => setStep(1)} style={{ flex: 1, padding: '12px 16px', background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 8, color: 'rgba(0,212,255,0.7)', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <ArrowLeftIcon style={{ width: 14, height: 14 }} /> Voltar
                </button>
                <button type="submit" style={{ flex: 2, padding: '12px 16px', background: 'linear-gradient(135deg,#00b4d8,#00d4ff)', border: 'none', borderRadius: 8, color: '#0a0f1e', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 24px rgba(0,212,255,0.35)' }}>
                  Próximo <ArrowRightIcon style={{ width: 15, height: 15 }} />
                </button>
              </div>
            </form>
          )}

          {/* ── ETAPA 3: Questionário ─────────────────────────────── */}
          {step === 3 && (
            <div>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 24, lineHeight: 1.6 }}>
                Responda as perguntas abaixo para identificarmos seu perfil de investidor. Suas respostas são confidenciais e ajudam a personalizar sua experiência.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {QUESTIONS.map((q) => (
                  <div key={q.key}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 10, lineHeight: 1.5 }}>{q.question}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {q.options.map((opt, idx) => {
                        const val = idx + 1;
                        const selected = answers[q.key] === val;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setAnswer(q.key, val)}
                            style={{
                              padding: '10px 14px', borderRadius: 8, textAlign: 'left', fontSize: 12, cursor: 'pointer', transition: 'all 0.15s',
                              background: selected ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.03)',
                              border: selected ? '1px solid rgba(0,212,255,0.5)' : '1px solid rgba(255,255,255,0.07)',
                              color: selected ? '#00d4ff' : 'rgba(255,255,255,0.6)',
                              fontWeight: selected ? 600 : 400,
                              display: 'flex', alignItems: 'center', gap: 10,
                            }}
                          >
                            <span style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, background: selected ? '#00d4ff' : 'rgba(255,255,255,0.06)', color: selected ? '#0a0f1e' : 'rgba(255,255,255,0.3)', border: selected ? 'none' : '1px solid rgba(255,255,255,0.1)' }}>
                              {val}
                            </span>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview do perfil */}
              {profile && (
                <div style={{ marginTop: 24, padding: '16px 20px', borderRadius: 10, background: 'rgba(0,0,0,0.2)', border: `1px solid ${profile.color}33`, animation: 'fadeSlideUp 0.4s ease-out both' }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase' }}>Seu perfil calculado</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: profile.color }}>{profile.label}</span>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', padding: '3px 10px', background: `${profile.color}18`, border: `1px solid ${profile.color}33`, borderRadius: 20 }}>score {score}/28</span>
                  </div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>{profile.desc}</p>
                </div>
              )}

              {/* Progresso */}
              <div style={{ marginTop: 20, fontSize: 11, color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
                {answeredCount}/7 perguntas respondidas
              </div>
              <div style={{ marginTop: 8, height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <div style={{ height: '100%', background: 'linear-gradient(90deg, #00b4d8, #00d4ff)', borderRadius: 2, width: `${(answeredCount / 7) * 100}%`, transition: 'width 0.3s' }} />
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <button type="button" onClick={() => setStep(2)} style={{ flex: 1, padding: '12px 16px', background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 8, color: 'rgba(0,212,255,0.7)', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <ArrowLeftIcon style={{ width: 14, height: 14 }} /> Voltar
                </button>
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={answeredCount < 7 || submitting}
                  style={{ flex: 2, padding: '12px 16px', background: answeredCount < 7 || submitting ? 'rgba(0,212,255,0.2)' : 'linear-gradient(135deg,#00b4d8,#00d4ff)', border: 'none', borderRadius: 8, color: answeredCount < 7 || submitting ? 'rgba(0,0,0,0.4)' : '#0a0f1e', fontWeight: 700, fontSize: 13, cursor: answeredCount < 7 || submitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: answeredCount === 7 && !submitting ? '0 4px 24px rgba(0,212,255,0.35)' : 'none', transition: 'all 0.2s' }}
                >
                  {submitting
                    ? <><span style={{ width: 14, height: 14, border: '2px solid rgba(0,0,0,0.3)', borderTopColor: '#0a0f1e', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} /> Criando...</>
                    : <><CheckCircleIcon style={{ width: 15, height: 15 }} /> Concluir Cadastro</>
                  }
                </button>
              </div>
            </div>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 10, color: 'rgba(255,255,255,0.15)', letterSpacing: 1 }}>
          © {new Date().getFullYear()} INCLUIRTECH · TODOS OS DIREITOS RESERVADOS
        </p>
      </div>

      <style>{`
        @keyframes gridScroll { from { background-position: 0 0; } to { background-position: 50px 50px; } }
        @keyframes orb1 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(-3%,4%) scale(1.05); } 66% { transform: translate(2%,-2%) scale(0.97); } }
        @keyframes orb2 { 0%,100% { transform: translate(0,0) scale(1); } 40% { transform: translate(4%,-5%) scale(1.08); } 70% { transform: translate(-2%,3%) scale(0.95); } }
        @keyframes particleUp { 0% { transform: translateY(0) translateX(0); opacity: 0; } 8% { opacity: 0.8; } 88% { opacity: 0.5; } 100% { transform: translateY(-130px) translateX(var(--drift)); opacity: 0; } }
        @keyframes logoFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(255,255,255,0.2); }
        input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px rgba(0,0,0,0.6) inset !important; -webkit-text-fill-color: #fff !important; caret-color: #fff; }
      `}</style>
    </div>
  );
}
